import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import {
  CihAtAGlanceCard,
  type AtAGlanceRow,
} from "~/components/cih/at-a-glance-card";
import { CihClosingCta } from "~/components/cih/closing-cta";
import type { CihCoachCellData } from "~/components/cih/coach-cell";
import { CihCoachGrid } from "~/components/cih/coach-grid";
import { CihFaqSection } from "~/components/cih/faq-section";
import { CihFrameworkCards } from "~/components/cih/framework-cards";
import { CihHero } from "~/components/cih/hero";
import { CihPullQuoteBand } from "~/components/cih/pull-quote-band";
import { RelatedEntityPills } from "~/components/cih/related-entity-pills";
import { CihSection } from "~/components/cih/section";
import { CihSectionHeader } from "~/components/cih/section-header";
import { CihStatStrip } from "~/components/cih/stat-strip";
import { CihTabNav, type CihTab } from "~/components/cih/tab-nav";
import { Button, ButtonLink } from "~/components/ui/button";
import { RichTextViewer } from "~/components/ui/rich-text-editor";
import {
  getChallengesByCtpFn,
  getCtpAccreditorsFn,
  getCtpFrameworksFn,
} from "~/lib/functions/cih/ctps";
import { getCoachesByCtpSlug, getCtpBySlug } from "~/lib/functions/ctps";
import { dropRestatedOpeningHeading } from "~/lib/shared/cih/body-toc";
import { buildStatStrip } from "~/lib/shared/cih/stat-strip";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
  createCourseStructuredData,
  createFaqStructuredData,
} from "~/lib/shared/meta";

type CtpData = NonNullable<Awaited<ReturnType<typeof getCtpBySlug>>["ctp"]>;
type CoachRow = Awaited<
  ReturnType<typeof getCoachesByCtpSlug>
>["coaches"][number];
type FrameworkRow = Awaited<ReturnType<typeof getCtpFrameworksFn>>[number];
type ChallengeLink = Awaited<ReturnType<typeof getChallengesByCtpFn>>[number];

type AccreditorLink = { id: string; slug: string | null; name: string };

type LoaderData = {
  title: string;
  ctp: CtpData;
  coaches: CoachRow[];
  coachesTotal: number;
  frameworks: FrameworkRow[];
  challenges: ChallengeLink[];
  accreditors: AccreditorLink[];
};

/** Safely parse a URL hostname; returns null when the input is not a valid URL. */
function safeHostname(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

export const Route = createFileRoute("/_public/training-programs/$slug/")({
  staticData: { title: "Training Program" },
  loader: async ({ params }): Promise<LoaderData> => {
    const { ctp } = await getCtpBySlug({ data: { slug: params.slug } });
    if (!ctp) throw notFound();
    const [coachesResult, frameworks, challenges, accreditors] =
      await Promise.all([
        getCoachesByCtpSlug({ data: { slug: params.slug, page: 1, limit: 6 } }),
        getCtpFrameworksFn({ data: { ctpId: ctp.id } }),
        getChallengesByCtpFn({ data: { ctpId: ctp.id } }),
        getCtpAccreditorsFn({ data: { ctpId: ctp.id } }),
      ]);
    return {
      title: ctp.name,
      ctp,
      coaches: coachesResult.coaches,
      coachesTotal: coachesResult.total,
      frameworks,
      challenges,
      accreditors,
    };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { slug: string };
  }) => {
    const name = loaderData?.ctp.name ?? params.slug;
    const url = `https://dreamcoachmatch.com/training-programs/${params.slug}`;
    const base = createBrandMeta({
      title: `${name} | Coaching Training Programs`,
      description:
        loaderData?.ctp.description || `Discover coaches trained at ${name}.`,
      ogUrl: url,
      ogImage: loaderData?.ctp.displayLogoUrl ?? undefined,
      canonical: url,
    });
    if (!loaderData) return base;

    const ctp = loaderData.ctp;
    const courseLd = createCourseStructuredData({
      name: ctp.name,
      url,
      description: ctp.description ?? undefined,
      image: ctp.displayLogoUrl ?? undefined,
      providerName: ctp.cco.name,
      providerUrl: ctp.cco.slug
        ? `https://dreamcoachmatch.com/organizations/${ctp.cco.slug}`
        : undefined,
    });
    const breadcrumbLd = createBreadcrumbStructuredData([
      { name: "Home", url: "https://dreamcoachmatch.com" },
      {
        name: "Training programs",
        url: "https://dreamcoachmatch.com/training-programs",
      },
      { name: ctp.name, url },
    ]);
    const faqLd =
      (ctp.faqs?.length ?? 0) > 0
        ? createFaqStructuredData(ctp.faqs ?? [])
        : null;

    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(courseLd) },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
        ...(faqLd
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(faqLd),
              },
            ]
          : []),
      ],
    };
  },
  component: TrainingProgramDetailPage,
});

function TrainingProgramDetailPage() {
  const { ctp, coaches, coachesTotal, frameworks, challenges, accreditors } =
    Route.useLoaderData();
  const { slug } = Route.useParams();

  const challengePills = challenges
    .filter((c): c is ChallengeLink & { slug: string } => c.slug != null)
    .map((c) => ({ label: c.title, href: `/coaching-for/${c.slug}` }));

  const websiteHost = safeHostname(ctp.displayWebsite);

  const atAGlanceRows: AtAGlanceRow[] = [
    // `ctp.cco` is the school that DELIVERS this program, so it is labelled as
    // the provider. The bodies that accredit it are a separate relationship
    // and get their own row below when any are recorded.
    {
      key: "Provider",
      value: ctp.cco.name,
      href: ctp.cco.slug ? `/organizations/${ctp.cco.slug}` : undefined,
    },
    ...(accreditors.length > 0
      ? [
          {
            key: "Accredited by",
            value: accreditors.map((a) => a.name).join(", "),
            // One row carries one href, so link only when a single
            // organization owns the whole value. With several names joined,
            // a link would send every one of them to the first body's page.
            href:
              accreditors.length === 1 && accreditors[0].slug
                ? `/organizations/${accreditors[0].slug}`
                : undefined,
          },
        ]
      : []),
    { key: "Format", value: ctp.deliveryFormat },
    { key: "ICF hours", value: ctp.icfHours },
    { key: "Duration", value: ctp.duration },
    { key: "Tuition", value: ctp.tuitionRange },
    { key: "Prerequisites", value: ctp.prerequisites },
  ];

  const statCells = buildStatStrip({
    narrativeStats: ctp.narrativeStats,
    hidePlatformStats: ctp.hidePlatformStats,
    derivedStats: [
      { value: ctp.icfHours, label: "ICF hours" },
      { value: ctp.duration, label: "Duration" },
    ],
  });

  const faqs = ctp.faqs ?? [];

  const tabs: CihTab[] = [
    { id: "overview", label: "Overview", count: ctp.body ? undefined : 0 },
    { id: "approach", label: "Approach", count: ctp.pullQuote ? undefined : 0 },
    { id: "frameworks", label: "Frameworks", count: frameworks.length },
    { id: "applied-to", label: "Applied to", count: challengePills.length },
    {
      id: "coaches",
      label: "Coaches",
      count: coachesTotal > 0 ? undefined : 0,
    },
    { id: "faq", label: "FAQ", count: faqs.length },
  ];

  // Adapt the CTP-specific coach shape into CihCoachCellData.
  const coachCells: CihCoachCellData[] = coaches.map(({ coach, profile }) => ({
    id: coach.id,
    username: coach.username,
    fullName: coach.fullName,
    profilePictureUrl: coach.profilePictureUrl,
    credential: profile?.coachingTitle ?? profile?.title ?? null,
    description: null,
  }));

  return (
    <>
      <CihHero
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Training programs", href: "/training-programs" },
          { label: ctp.name, href: `/training-programs/${slug}` },
        ]}
        kicker="Training program"
        kickerColorClass="text-orange-700"
        title={ctp.name}
        lede={ctp.description}
        logoUrl={ctp.displayLogoUrl}
        cta={
          <>
            <Link to="/discovery" search={{ source: `ctp:${slug}` }}>
              <Button variant="primary">Find a coach →</Button>
            </Link>
            {ctp.displayWebsite && websiteHost ? (
              <ButtonLink
                href={ctp.displayWebsite}
                target="_blank"
                variant="ghost"
              >
                Visit {websiteHost}
                <ExternalLink className="ml-1.5 inline h-4 w-4" />
              </ButtonLink>
            ) : null}
          </>
        }
        meta={<CihAtAGlanceCard rows={atAGlanceRows} />}
      />

      <CihStatStrip cells={statCells} />
      <CihTabNav tabs={tabs} />

      {ctp.body ? (
        <CihSection id="overview" divider={false}>
          <CihSectionHeader label="Overview" title="About" />
          <div className="mx-auto max-w-7xl px-4 xl:px-0">
            <div className="prose prose-lg max-w-none">
              <RichTextViewer
                content={JSON.stringify(dropRestatedOpeningHeading(ctp.body))}
              />
            </div>
          </div>
        </CihSection>
      ) : null}

      {ctp.pullQuote ? (
        <div id="approach">
          <CihPullQuoteBand
            quote={ctp.pullQuote}
            attribution={ctp.pullQuoteAttribution}
          />
        </div>
      ) : null}

      {frameworks.length > 0 ? (
        <CihSection id="frameworks" divider={!ctp.pullQuote}>
          <CihFrameworkCards
            frameworks={frameworks}
            sectionLabel="Frameworks"
            heading={`Frameworks taught at ${ctp.name}`}
          />
        </CihSection>
      ) : null}

      {challengePills.length > 0 ? (
        <CihSection id="applied-to">
          <RelatedEntityPills
            items={challengePills}
            sectionLabel="Applied to"
            heading={`Challenges this training prepares coaches for`}
          />
        </CihSection>
      ) : null}

      {coachCells.length > 0 ? (
        <CihSection id="coaches">
          <CihCoachGrid
            coaches={coachCells}
            totalCount={coachesTotal}
            sectionLabel="Coaches"
            heading={`${ctp.name} coaches on Dream Coach Match`}
            seeAllHref={`/training-programs/${slug}/coaches`}
          />
        </CihSection>
      ) : null}

      {faqs.length > 0 ? (
        <CihSection id="faq">
          <CihFaqSection faqs={faqs} heading={`Questions about ${ctp.name}`} />
        </CihSection>
      ) : null}

      <CihClosingCta
        headline={`Find a coach trained at ${ctp.name}.`}
        sub="Free · 5 minutes · No commitment"
        cta={
          <Link to="/discovery" search={{ source: `ctp:${slug}` }}>
            <Button variant="primary" size="lg">
              Start →
            </Button>
          </Link>
        }
      />
    </>
  );
}
